import { useEffect, useState, useCallback, useMemo, createElement } from 'react'
import { FaCheck } from 'react-icons/fa';
import * as translator from '../utils/translator';
import { useCartContext } from '../customHooks/useCart';
import { useParams, useSearchParams } from 'react-router-dom';
import { isPostCheckoutVerificationCountry } from '../utils/postCheckoutVerification';

type Props = {
    stage: string;
    language: string;
    isMobile?: boolean;
    flow: string;
    carePlusSteps?: boolean;
}

const ORANGE_BAR = 'bg-gradient-to-r from-[#F59200] to-[#FFBB29]';

const StepsListV2: React.FC<Props> = ({ stage, language, isMobile, flow, carePlusSteps }) => {

    const [currentStage, setCurrentStage] = useState<number>(1);
    const {getCart} = useCartContext();
    const [searchParams] = useSearchParams();
    const { category, country } = useParams();
    const cart = getCart();

    const isSchengen = cart.flow === "schengen" || cart.mention === "schengen" || category === "schengen";
    const isSwissCannabis = (cart.mention?.includes("medical_cannabis") || category?.includes("medical_cannabis")) && country?.toUpperCase() === "CH";
    // CH and UK verify identity post-purchase in My Account
    const isPostCheckoutCountry = isPostCheckoutVerificationCountry(country);



    const stagesOrderMapping = useMemo(() => {
        const mapping: string[] = ['Questionnaire'];

        // If the catalog is ch cannabis, we need id verifcation before the treatment
        if (isSwissCannabis) {
            if (cart.should_id_verify || stage === 'CustomerVerification') {
                mapping.push('CustomerVerification');
            }
            mapping.push('Treatment');
        } else {
            if (!isSchengen) {
                mapping.push('Treatment');
            }
            if (!isPostCheckoutCountry && (cart.should_id_verify || stage === 'CustomerVerification')) {
                mapping.push('CustomerVerification');
            }
        }

        if (cart.consultation_type === 'video' || stage === 'chooseDoctorAndAppointment' || searchParams.get("ct") === "video") {
            mapping.push('chooseDoctorAndAppointment');
        }

        mapping.push('Shipping');
        mapping.push('Payment');

        return mapping.reduce((acc, stage, index) => {
            acc[stage] = index + 1;
            return acc;
        }, {} as Record<string, number>);
        
    }, [cart.consultation_type, cart.should_id_verify, isSwissCannabis, isPostCheckoutCountry]);

    const getCurrentStage = useCallback((stage: string) => {
        if (stage === "Questionnaire") setCurrentStage(stagesOrderMapping['Questionnaire']);
        if (stage === "Treatment") setCurrentStage(stagesOrderMapping['Treatment']);

        if (stage === "SignUp") {
            if (stagesOrderMapping['CustomerVerification']) {
                setCurrentStage(stagesOrderMapping['CustomerVerification']);
            } else if (stagesOrderMapping['chooseDoctorAndAppointment']) {
                setCurrentStage(stagesOrderMapping['chooseDoctorAndAppointment']);
            } else {
                setCurrentStage(stagesOrderMapping['Shipping']);
            }
        }

        if (stage === "CustomerVerification") setCurrentStage(stagesOrderMapping['CustomerVerification']);
        if (stage === "chooseDoctorAndAppointment") setCurrentStage(stagesOrderMapping['chooseDoctorAndAppointment']);
        if (stage === "Shipping") setCurrentStage(stagesOrderMapping['Shipping']);
        if (stage === "Payment") setCurrentStage(stagesOrderMapping['Payment']);
    }, [stagesOrderMapping]);

    useEffect(() => {
        getCurrentStage(stage);
    }, [currentStage, stage, cart.consultation_type]);

    const customerVerificationStep = {
        id: stagesOrderMapping['CustomerVerification'], 
        text: translator.translate(language, "default", `StepsList/${flow}/customer_verification`), 
        progressBarColorClassName: 'bg-[#60BA4F] shadow-[0_0_3px_#51B851]', 
        checkIconClassName: 'bg-[#60BA4F]' 
    };

    const treatmentStep = {
        id: stagesOrderMapping['Treatment'], 
        text: translator.translate(language, "default", `StepsList/${flow}/treatment`), 
        progressBarColorClassName: 'bg-[#3DAC5F] shadow-[0_0_3px_#51B851]', 
        checkIconClassName: 'bg-[#3DAC5F]' 
    };

    const showVerification = !isPostCheckoutCountry && (cart.should_id_verify || stage === 'CustomerVerification');
    const showVideoConsultation = cart.consultation_type === 'video' || stage === 'chooseDoctorAndAppointment' || searchParams.get("ct") === "video";

    // Define steps for easier management
    const steps = useMemo(() => [
        { 
            id: stagesOrderMapping['Questionnaire'], 
            text: translator.translate(language, "default", `StepsList/${flow}/questionnaire`), 
            progressBarColorClassName: 'bg-[#1F9874] shadow-[0_0_3px_#3DAC5F]', 
            checkIconClassName: 'bg-[#1F9874]' 
        },
        ...(isSwissCannabis ? [
            ...(showVerification ? [customerVerificationStep] : []),
            treatmentStep,
        ] : [
            ...(!isSchengen ? [treatmentStep] : []),
            ...(showVerification ? [customerVerificationStep] : []),
        ]),
        ...(showVideoConsultation ? [{ 
            id: stagesOrderMapping['chooseDoctorAndAppointment'], 
            text: translator.translate(language, "default", `StepsList/${flow}/doctor_appointment`), 
            progressBarColorClassName: 'bg-[#60BA4F] shadow-[0_0_3px_#51B851]', 
            checkIconClassName: 'bg-[#60BA4F]' 
        }] : []),
        { 
            id: stagesOrderMapping['Shipping'], 
            text: translator.translate(language, "default", `StepsList/${flow}/shipping`), 
            progressBarColorClassName: 'bg-[#79C83F] shadow-[0_0_3px_#79C83F]', 
            checkIconClassName: 'bg-[#79C83F]' 
        },
        { 
            id: stagesOrderMapping['Payment'], 
            text: translator.translate(language, "default", `StepsList/${flow}/payment`), 
            progressBarColorClassName: 'bg-[#93D530]', 
            checkIconClassName: 'bg-[#93D530]' 
        }
    ], [stagesOrderMapping, language, flow, cart.should_id_verify]);

    // Get current step text
    const getCurrentStepText = () => {
        const currentStep = steps.find(step => step.id === currentStage);
        return currentStep ? currentStep.text : '';
    };
 
    return (
        <div id="steps-list-container" className={`w-full flex p-4 gap-4 md:gap-0 md:p-0 justify-center ${carePlusSteps ? 'bg-white' : 'bg-[#EAEEE9]'} ${isMobile ? 'flex-col justify-start' : ''}`}>
            {isMobile && 
            <div id='mobile-current-step-container' className='flex justify-between items-center'>
                <p id='mobile-current-step-text' className={`font-roboto w-fit text-center text-black font-medium`}>{getCurrentStepText()}</p>
                <span id='mobile-current-step-number' className={`font-roboto w-fit text-center text-black font-medium`}>{currentStage}/{steps.length}</span>
            </div>
            }
            <div id='steps-list' className='flex flex-row w-full md:w-[70%] justify-center gap-2'>
                {steps.map((step) => (
                    createElement(Step, {
                        key: step.text,
                        isCurrentStep: currentStage === step.id,
                        text: step.text,
                        progressBarColorClassName: step.progressBarColorClassName,
                        checkIconClassName: step.checkIconClassName,
                        isChecked: step.id < currentStage,
                        isMobile: isMobile,
                        carePlusSteps: carePlusSteps
                    })
                ))}
            </div>
        </div>
    )
}


type StepProps = {
    text: string;
    isChecked: boolean;
    isCurrentStep: boolean;
    isMobile?: boolean;
    progressBarColorClassName?: string;
    checkIconClassName?: string;
    carePlusSteps?: boolean;
}

function Step({ text, progressBarColorClassName, isChecked, isCurrentStep, checkIconClassName, isMobile, carePlusSteps }: StepProps) {

    const isFutureStep = !isChecked && !isCurrentStep;
    const effectiveBarClass = carePlusSteps ? ORANGE_BAR : progressBarColorClassName;

    return (
        <div id='step-container' className='flex w-full flex-col relative items-center justify-center md:h-[60px]'>
            {!isMobile ? <p id='step-text' className={`font-roboto text-center ${isFutureStep ? 'text-[#9DA19E]' : 'text-black'} ${isCurrentStep ? 'font-semibold' : 'font-normal'} text-sm`}>{text}</p> : null}
            <div id='step-progress-bar' className={`w-full h-[6px] md:absolute md:bottom-0 rounded-full flex items-center justify-center ${isFutureStep ? (carePlusSteps ? 'bg-[#dee3ed]' : 'bg-white') : effectiveBarClass}`}>
                {isChecked && !carePlusSteps &&
                <div id='step-check-icon-container' className={`flex  items-center justify-center w-7 h-7 p-2 rounded-full ${checkIconClassName}`}>
                    {createElement(FaCheck, { className: 'text-white rounded-full' })}
                </div>
                }
            </div>
        </div>
    )
}

export default StepsListV2